import React, { useState } from 'react';
import { FaExpand, FaTimes, FaChevronLeft, FaChevronRight } from 'react-icons/fa';

const Gallery: React.FC = () => {
    const [selectedImage, setSelectedImage] = useState<number | null>(null);

    const images = [
        { src: '/images/IMG_20251210_174329.jpg', alt: 'Resort Exterior' },
        { src: '/images/IMG_20251210_174421.jpg', alt: 'Swimming Pool Area' },
        { src: '/images/IMG_20251210_175521.jpg', alt: 'Garden Landscape' },
        { src: '/images/IMG_20251210_175845.jpg', alt: 'Outdoor Seating' },
        { src: '/images/IMG_20251210_180546.jpg', alt: 'Resort Facilities' },
        { src: '/images/IMG_20251210_173800.jpg', alt: 'Resort Overview' },
        { src: '/images/IMG_20251210_173811.jpg', alt: 'Resort Ambiance' },
    ];

    const openLightbox = (index: number) => {
        setSelectedImage(index);
        document.body.style.overflow = 'hidden'; // Prevent background scrolling
    };

    const closeLightbox = () => {
        setSelectedImage(null);
        document.body.style.overflow = 'unset'; // Restore scrolling
    };

    const nextImage = () => {
        if (selectedImage !== null) {
            setSelectedImage((selectedImage + 1) % images.length);
        }
    };

    const prevImage = () => {
        if (selectedImage !== null) {
            setSelectedImage(selectedImage === 0 ? images.length - 1 : selectedImage - 1);
        }
    };

    // Handle keyboard navigation
    React.useEffect(() => {
        const handleKeyPress = (e: KeyboardEvent) => {
            if (selectedImage !== null) {
                if (e.key === 'Escape') closeLightbox();
                if (e.key === 'ArrowRight') nextImage();
                if (e.key === 'ArrowLeft') prevImage();
            }
        };

        window.addEventListener('keydown', handleKeyPress);
        return () => window.removeEventListener('keydown', handleKeyPress);
    }, [selectedImage]);

    return (
        <div className="min-h-screen bg-background">
            {/* Header */}
            <section className="bg-gradient-to-r from-primary to-primary-dark text-white py-16">
                <div className="container mx-auto px-4 text-center">
                    <h1 className="text-5xl font-bold mb-4">Resort Gallery</h1>
                    <p className="text-xl opacity-90 max-w-2xl mx-auto">
                        Explore the beauty and comfort of Sunrise Agri Resorts
                    </p>
                </div>
            </section>

            <div className="container mx-auto px-4 py-16">
                {/* Gallery Grid - Same style as Home page */}
                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
                    {images.map((image, index) => (
                        <div 
                            key={index} 
                            className="relative aspect-[4/3] overflow-hidden rounded-xl shadow-medium group cursor-pointer"
                            onClick={() => openLightbox(index)}
                        >
                            <img 
                                src={image.src} 
                                alt={image.alt}
                                className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-500"
                            />
                            <div className="absolute inset-0 bg-black bg-opacity-0 group-hover:bg-opacity-30 transition-all duration-300 flex items-center justify-center">
                                <FaExpand className="text-white text-2xl opacity-0 group-hover:opacity-100 transition-opacity duration-300" />
                            </div>
                        </div>
                    ))}
                </div>

                {/* Lightbox - Fixed for proper image display */}
                {selectedImage !== null && (
                    <div 
                        className="fixed inset-0 bg-black bg-opacity-95 z-50 flex items-center justify-center p-4"
                        onClick={closeLightbox}
                    >
                        <div 
                            className="relative w-full h-full flex items-center justify-center"
                            onClick={(e) => e.stopPropagation()}
                        >
                            <img 
                                src={images[selectedImage].src} 
                                alt={images[selectedImage].alt}
                                className="max-w-full max-h-full object-contain"
                                style={{ maxWidth: '95vw', maxHeight: '95vh' }}
                            />
                            
                            {/* Close Button */}
                            <button
                                onClick={closeLightbox}
                                className="absolute top-4 right-4 text-white bg-black bg-opacity-50 hover:bg-opacity-70 rounded-full p-3 transition-all duration-200 z-10"
                                aria-label="Close gallery"
                            >
                                <FaTimes className="text-xl" />
                            </button>

                            {/* Navigation Buttons */}
                            {images.length > 1 && (
                                <>
                                    <button
                                        onClick={prevImage}
                                        className="absolute left-4 top-1/2 transform -translate-y-1/2 text-white bg-black bg-opacity-50 hover:bg-opacity-70 rounded-full p-3 transition-all duration-200"
                                        aria-label="Previous image"
                                    >
                                        <FaChevronLeft className="text-xl" />
                                    </button>
                                    <button
                                        onClick={nextImage}
                                        className="absolute right-4 top-1/2 transform -translate-y-1/2 text-white bg-black bg-opacity-50 hover:bg-opacity-70 rounded-full p-3 transition-all duration-200"
                                        aria-label="Next image"
                                    >
                                        <FaChevronRight className="text-xl" />
                                    </button>
                                </>
                            )}
                        </div>
                    </div>
                )}

                {/* Call to Action */}
                <div className="text-center mt-16">
                    <div className="bg-gradient-to-r from-primary to-primary-dark rounded-3xl p-12 text-white">
                        <h2 className="text-4xl font-bold mb-4">Ready to Experience This Beauty?</h2>
                        <p className="text-xl mb-8 opacity-90 max-w-2xl mx-auto">
                            Book your stay now and enjoy all these amazing facilities and scenic views in person
                        </p>
                        <div className="flex flex-col sm:flex-row gap-4 justify-center">
                            <a 
                                href="/book-room" 
                                className="bg-white text-primary hover:bg-gray-100 px-8 py-4 rounded-full font-semibold text-lg transition-all duration-300 shadow-medium hover:shadow-large transform hover:-translate-y-1"
                            >
                                Book Your Stay
                            </a>
                            <a 
                                href="/contact" 
                                className="border-2 border-white text-white hover:bg-white hover:text-primary px-8 py-4 rounded-full font-semibold text-lg transition-all duration-300"
                            >
                                Contact Us
                            </a>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default Gallery;